import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCr, timeAgo } from "@/lib/utils-ros";
import { useAuth } from "@/lib/auth";
import { Wallet, CheckCircle2, Clock, Loader2, Filter, Check } from "lucide-react";
import { PageToolbar } from "@/components/revenueos/PageToolbar";
import { toast } from "sonner";

const STATUS_META = {
  open: { color: "#C76500", background: "#FFF3E2" },
  released: { color: "#047857", background: "#E7F8F1" },
  cancelled: { color: "#607D8B", background: "#EDF2F4" },
};

export default function Finance() {
  const { canWrite } = useAuth();
  const canRelease = canWrite("finance");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await api.get("/finance/notes", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Could not load finance notes");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [filter]);

  async function markReleased(id) {
    setBusy(id);
    try {
      await api.patch(`/finance/notes/${id}`, { status: "released" });
      toast.success("Marked as released");
      await load();
    } catch {
      toast.error("Update failed");
    } finally {
      setBusy(null);
    }
  }

  const filtered = items.filter((n) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [n.po_number, n.company_name, n.partner_name, n.message, n.payment_type].filter(Boolean).join(" ").toLowerCase().includes(q);
  });

  const openTotal = items.filter((n) => n.status === "open").reduce((s, n) => s + (n.commission_due_cr || 0), 0);

  return (
    <div className="min-h-full bg-[#F5FAFC] pb-12" data-testid="finance-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-7 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight !text-white">Finance</h1>
            <p className="text-[13px] text-white/75 mt-1">
            Partner commission releases — auto-raised when customer payments are received.
            </p>
          </div>
          <div className="text-left md:text-right">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Open releases</div>
            <div className="font-display text-2xl tabular-nums !text-white">{filter === "open" ? formatCr(openTotal) : "—"}</div>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-5">
        <PageToolbar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search by PO, partner, or company…"
          entityLabel="commission releases"
          count={filtered.length}
          variant="universe"
          right={(
            <div className="relative">
              <button
                type="button"
                onClick={() => setFilterOpen(open => !open)}
                data-testid="finance-filter-btn"
                className={`h-9 px-3.5 rounded-full border text-[12px] font-semibold flex items-center gap-2 shadow-[0_5px_14px_rgba(7,83,154,0.08)] ${filter !== "all" ? "border-[#07539A] bg-[#E7F5FB] text-[#07539A]" : "border-[#BFD6E1] bg-white text-[#07539A] hover:bg-[#F3FAFD]"}`}
              >
                <Filter size={13} /> Filter
                {filter !== "all" && <span className="w-5 h-5 rounded-full bg-[#07539A] !text-white text-[10px] flex items-center justify-center">1</span>}
              </button>
              {filterOpen && (
                <div className="absolute right-0 top-11 z-30 w-52 rounded-2xl border border-[#C9DCE6] bg-white p-2 shadow-[0_18px_44px_rgba(7,83,154,0.18)]" data-testid="finance-filter-menu">
                  <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[#07539A]">Release status</div>
                  {[{ key: "all", label: "All releases" }, { key: "open", label: "Open" }, { key: "released", label: "Released" }].map(option => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => { setFilter(option.key); setFilterOpen(false); }}
                      data-testid={`finance-filter-${option.key}`}
                      className={`w-full px-3 py-2.5 rounded-xl flex items-center text-left text-[12px] ${filter === option.key ? "bg-[#E7F5FB] text-[#07539A] font-semibold" : "text-[#243B53] hover:bg-[#F3FAFD]"}`}
                    >
                      <span className="flex-1">{option.label}</span>
                      {filter === option.key && <Check size={13} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        />

      {loading ? (
        <div className="bg-white border border-[#C9DCE6] rounded-[22px] text-[#78909C] p-10 text-center shadow-[0_8px_22px_rgba(7,83,154,0.06)]">Loading finance notes…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center p-10 border border-dashed border-[#B8D3DF] rounded-[22px] bg-white text-[13px] text-[#607D8B] shadow-[0_8px_22px_rgba(7,83,154,0.04)]">
          <span className="w-11 h-11 rounded-xl bg-[#E7F5FB] text-[#07539A] flex items-center justify-center mx-auto mb-3"><Wallet size={20} /></span>
          No {filter === "all" ? "" : filter + " "}commission release notes.
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((n) => (
            <div key={n.id} data-testid={`finance-note-${n.id}`} className="p-4 md:p-5 flex items-start gap-3 border border-[#C9DCE6] rounded-[22px] bg-white shadow-[0_8px_22px_rgba(7,83,154,0.06)]">
              <span className="w-9 h-9 rounded-xl bg-[#E7F5FB] text-[#07539A] flex items-center justify-center shrink-0"><Wallet size={15} /></span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-mono text-[11px] text-[#07539A]">{n.po_number}</span>
                  <span className="text-[9px] uppercase tracking-wider text-[#607D8B]">{n.payment_type}</span>
                  <span
                    className="text-[9px] uppercase tracking-wider px-2.5 py-1 rounded-full"
                    style={STATUS_META[n.status] || STATUS_META.cancelled}
                  >
                    {n.status}
                  </span>
                </div>
                <div className="text-[13px] font-medium text-[#243B53]">{n.message}</div>
                <div className="text-[11px] text-[#78909C] mt-1">
                  {n.company_name} · {n.partner_name} · {timeAgo(n.created_at)}
                </div>
                <div className="text-[12px] font-mono tabular-nums mt-2 text-[#07539A]">
                  Release {formatCr(n.commission_due_cr)}
                  <span className="text-[#78909C] font-sans text-[11px] ml-2">
                    on {formatCr(n.payment_received_cr)} received ({n.commission_rate_pct}%)
                  </span>
                </div>
              </div>
              {n.status === "open" && canRelease && (
                <button
                  onClick={() => markReleased(n.id)}
                  disabled={busy === n.id}
                  data-testid={`finance-release-${n.id}`}
                  className="h-9 px-3.5 rounded-xl bg-[#047857] hover:bg-[#036B4D] !text-white text-[11px] font-semibold flex items-center gap-1.5 disabled:opacity-50 shrink-0 shadow-[0_5px_14px_rgba(4,120,87,0.16)]"
                >
                  {busy === n.id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                  Mark released
                </button>
              )}
              {n.status === "released" && (
                <Clock size={14} className="text-[#047857] shrink-0 mt-1" />
              )}
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
